"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import type { BaseTenantId } from "@tilli-internal/tenant-data";
import { useT } from "@tillix/i18n/react";
import { Button } from "@tillix/ui-atoms/button";
import { X } from "lucide-react";
import { observer } from "mobx-react-lite";
import { isAppBannerSnoozed, snoozeAppBanner } from "./app-banner-snooze";
import { MOBILE_APP_STORE_LINKS } from "./consts";
import { detectMobilePlatform } from "./mobile-platform";

interface AppInstallBannerProps {
  tenantId: string;
}

/**
 * Invites phone and tablet visitors to the tenant's app.
 *
 * Shown only on a platform with a public store link (see `MOBILE_APP_STORE_LINKS`) and hidden for
 * `APP_BANNER_SNOOZE_DAYS` once dismissed or followed. It is a dismissible bar, not an interstitial:
 * Google ranks pages down for full-screen app-install prompts on mobile.
 */
export const AppInstallBanner = observer(function AppInstallBanner({
  tenantId,
}: AppInstallBannerProps) {
  const { t } = useT();
  const [storeLink, setStoreLink] = useState<string | null>(null);

  // the platform and the snooze live in the browser, so the server render never shows the banner
  useEffect(() => {
    const platform = detectMobilePlatform(
      navigator.userAgent,
      navigator.maxTouchPoints,
    );
    const link = platform
      ? MOBILE_APP_STORE_LINKS[tenantId as BaseTenantId]?.[platform]
      : undefined;

    if (link && !isAppBannerSnoozed()) setStoreLink(link);
  }, [tenantId]);

  if (!storeLink) return null;

  const handleDismiss = () => {
    snoozeAppBanner();
    setStoreLink(null);
  };

  return (
    <aside
      aria-label={t("appBanner.label")}
      className="fixed inset-x-0 bottom-0 z-50 flex items-center gap-3 border-border border-t bg-background p-4 shadow-lg"
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium text-foreground text-sm">
          {t("appBanner.title")}
        </p>
        <p className="text-muted-foreground text-xs">{t("appBanner.body")}</p>
      </div>
      <Button
        onClick={() => snoozeAppBanner()}
        render={<Link href={storeLink} rel="noopener noreferrer" />}
        size="sm"
      >
        {t("appBanner.getApp")}
      </Button>
      <Button
        aria-label={t("appBanner.dismiss")}
        onClick={handleDismiss}
        size="icon"
        type="button"
        variant="ghost"
      >
        <X aria-hidden="true" />
      </Button>
    </aside>
  );
});
